import * as React from 'react'

import {
  ChevronsDownIcon,
  ChevronsRightIcon,
  ChevronsUpDownIcon,
  ChevronsUpIcon,
  EyeIcon,
  EyeOffIcon,
  GripVerticalIcon,
  HistoryIcon,
  LinkIcon,
  PlusIcon,
  SearchIcon,
} from 'lucide-react'
import { action, computed, makeObservable, observable } from 'mobx'
import { inject, observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'
import { SortableContainer, SortableElement } from 'react-sortable-hoc'
import {
  AutoSizer,
  CellMeasurer,
  CellMeasurerCache,
  List,
} from 'react-virtualized'

import { MapFileType } from '~/client/graph'
import DesktopFileInput from '~/client/src/desktop/components/FileInput/DesktopFileInput'
import DesktopInitialState from '~/client/src/desktop/stores/DesktopInitialState'
import * as Icons from '~/client/src/shared/components/Icons'
import { mapFileDisplayName } from '~/client/src/shared/components/MapBoxViewer/globeMapFiles'
import { Button } from '~/client/src/shared/components/shadcn-ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '~/client/src/shared/components/shadcn-ui/popover'
import MapViewItemBase from '~/client/src/shared/components/SitemapHelpers/models/MapViewItemBase'
import StruxhubInput from '~/client/src/shared/components/StruxhubInputs/StruxhubInput'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import GlobeView from '~/client/src/shared/models/GlobeView'
import Sitemap from '~/client/src/shared/models/Sitemap'
import RemoteTilesetsStore from '~/client/src/shared/stores/domain/RemoteTilesets.store'
import SitemapsStore from '~/client/src/shared/stores/domain/Sitemaps.store'
import TilesetsStore from '~/client/src/shared/stores/domain/Tilesets.store'
import TilesetUploadProgressStore from '~/client/src/shared/stores/domain/TilesetUploadProgress.store'
import { isBaseSurfaceActive } from '~/client/src/shared/utils/keyboardClaim'
import { NOOP } from '~/client/src/shared/utils/noop'

import ProjectSetUpPageStore from '../../../../ProjectSetUpPage.store'
import MapViewSetUpStore, { SetUpSteps } from '../../MapViewSetUp.store'
import { ViewType } from '../../stores/MapBoxEditor.store'
import { ItemsCollapseState } from '../../stores/MapViewItemsSetup.store'
import HierarchyTreeNode from '../HierarchyTree/HierarchyTreeNode'
import { C_KEY_CODE, V_KEY_CODE } from '../SitemapEditor/MapEditor'
import GlobeLeftPanelStore, { IMapFile } from './GlobeLeftPanel.store'
import MapNotificationsPopoverContent from './MapNotificationsPopoverContent'

import '../LeftPanel.scss'

interface IProps {
  globes?: GlobeView[]

  store: MapViewSetUpStore
  projectSetUpPageStore?: ProjectSetUpPageStore
  step: SetUpSteps
  state?: DesktopInitialState

  sitemapsStore?: SitemapsStore
  tilesetsStore?: TilesetsStore
  remoteTilesetsStore?: RemoteTilesetsStore
  tilesetUploadProgressStore?: TilesetUploadProgressStore
  isDisabled?: boolean

  selectedMapViewItem?: MapViewItemBase
  selectedSitemap?: Sitemap
}

interface ISortableListItems {
  hiddenItems: JSX.Element[]
  shownItems: JSX.Element[]
}

interface ISortableListProps {
  isRubberMode?: boolean
  items: ISortableListItems
}

const SEARCH_DEFAULT = 'Search'
const OVERSCAN_ROW_COUNT = 6
const ROW_HEIGHT = 35
const objectsVisible = 'objects visible'

const SortableItem = SortableElement(({ item }) => (
  <div
    className={classList({
      'bg-white': true,
      hidden: !item?.props?.children,
    })}
  >
    {item}
  </div>
))

const SortableList = SortableContainer<ISortableListProps>(
  ({ items, isRubberMode }) => {
    const { hiddenItems, shownItems } = items
    return (
      <div className="col">
        {/* Shown items */}
        {shownItems?.length > 0 && (
          <div>
            <div className="row pb10 px16">
              <div className="text grey-30 bold lp1 line-16">
                {Localization.translator.visibleLayers.toLocaleUpperCase()}
              </div>
            </div>
            {shownItems.map((item, index) => {
              return isRubberMode ? (
                <div key={`hidden-item-${index}`}>{item}</div>
              ) : (
                <SortableItem key={`item-${index}`} index={index} item={item} />
              )
            })}
          </div>
        )}

        {/* Hidden items */}
        {hiddenItems?.length > 0 && (
          <div>
            <div className="row py10 px16">
              <div className="text grey-30 bold lp1 line-16">
                {Localization.translator.hiddenLayers.toLocaleUpperCase()}
              </div>
            </div>
            {hiddenItems.map((item, index) => (
              <div key={`hidden-item-${index}`}>{item}</div>
            ))}
          </div>
        )}
      </div>
    )
  },
)

const tileset = 'Tileset'
const remoteTileset = 'Remote Tileset'
const file = 'File'
const doneTypingInterval = 300

@inject(
  'state',
  'sitemapsStore',
  'tilesetsStore',
  'remoteTilesetsStore',
  'tilesetUploadProgressStore',
)
@observer
export default class GlobeLeftPanel extends React.Component<IProps> {
  @observable private mapViewSetUpStore: MapViewSetUpStore = null
  @observable private localProps: IProps = null
  @observable private hoveredMapFile: IMapFile = null
  private readonly cellMeasurerCache: CellMeasurerCache = null
  @observable private isCollapsed: boolean = false
  private store: GlobeLeftPanelStore
  public constructor(props: IProps) {
    super(props)

    this.store = new GlobeLeftPanelStore(
      props.store,
      props.sitemapsStore,
      props.tilesetsStore,
      props.remoteTilesetsStore,
      props.state,
    )

    this.cellMeasurerCache = new CellMeasurerCache({
      fixedWidth: true,
    })

    this.localProps = props
    this.mapViewSetUpStore = props.store
    makeObservable(this)
  }

  public componentDidMount(): void {
    document.addEventListener('keydown', this.onKeyDown)
  }

  public componentWillUnmount(): void {
    document.removeEventListener('keydown', this.onKeyDown)
  }

  public render(): JSX.Element {
    const {
      store: { mapBoxEditorStore },
      isDisabled,
    } = this.props
    const { selectedViewType } = mapBoxEditorStore

    if (this.isCollapsed) {
      return (
        <div
          className={classList({
            'sitemaps-left-bar br-light-grey full-height col no-outline-container collapsed-panel': true,
            'inactive-element': isDisabled,
          })}
        >
          <Button
            variant="ghost"
            size="icon"
            className="tw:self-center"
            aria-label={Localization.translator.expandPanel}
            onClick={this.toggleCollapseState}
          >
            <ChevronsRightIcon />
          </Button>
        </div>
      )
    }

    return (
      <div
        className={classList({
          'sitemaps-left-bar br-light-grey full-height col no-outline-container': true,
          'inactive-element': isDisabled,
        })}
      >
        {this.renderViewSelect()}
        {selectedViewType === ViewType.Objects && (
          <>
            {this.renderSearchBar()}
            <div className="sitemaps-left-bar-header no-grow row y-center bb-light-input-border">
              <div
                className="row text light large ml10 pointer"
                onClick={this.toggleCollapsing}
              >
                {this.navigationIcon}
                {`${this.store.getVisibleObjectsCount(
                  selectedViewType,
                )} ${objectsVisible}`}
              </div>
              <div className="no-grow"></div>
            </div>
          </>
        )}
        {this.renderHierarchyPanel()}
      </div>
    )
  }

  private toggleCollapseState = (): void => {
    this.isCollapsed = !this.isCollapsed
  }

  private renderViewSelect(): JSX.Element {
    const { selectedViewType } = this.mapViewSetUpStore.mapBoxEditorStore

    return (
      <div className="row left-panel-items bb-light-cool-grey">
        {Object.values(ViewType).map((viewType, index) => {
          const isSelected = selectedViewType === viewType
          if (
            !this.mapViewSetUpStore.isGlobeMode &&
            viewType === ViewType.MapFiles
          ) {
            return null
          }

          return (
            <div
              key={viewType}
              className={classList({
                'row x-center full-height bg-white left-panel-item': true,
                'bl-white br-white': index === 1,
                selected: isSelected,
                inactive: !isSelected,
              })}
              onClick={this.store.onViewTypeSelect.bind(null, viewType)}
            >
              <div
                className={classList({
                  'text large row y-center x-center no-grow full-height no-white-space-wrap': true,
                  'blue-brand selected-item': isSelected,
                  'primary pointer': !isSelected,
                })}
              >
                {viewType}
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  private changeSearchText = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    this.mapViewSetUpStore.mapViewItemsSetupStore.setSearchKey(
      event.target.value,
    )
    this.recomputeGridSize()
  }

  private renderSearchBar(): JSX.Element {
    return (
      <div className="search-bar relative bg-white px10">
        <SearchIcon size={20} className="search-bar-icon absolute" />
        <StruxhubInput
          onChange={this.changeSearchText}
          value={this.mapViewSetUpStore.mapViewItemsSetupStore.searchKey}
          placeholder={SEARCH_DEFAULT}
          isMinimalisticMode={true}
        />
      </div>
    )
  }

  private renderMapFilesSearchBar(): JSX.Element {
    return (
      <div className="search-bar relative bg-white px10">
        <SearchIcon size={20} className="search-bar-icon absolute" />
        <StruxhubInput
          onChange={this.changeSearchText}
          value={this.mapViewSetUpStore.mapViewItemsSetupStore.searchKey}
          placeholder={SEARCH_DEFAULT}
          isMinimalisticMode={true}
          withDelay={true}
          inputDelay={doneTypingInterval}
        />
      </div>
    )
  }

  private renderItemsPanel(): JSX.Element {
    const { visibleHierarchyList } =
      this.mapViewSetUpStore.mapViewItemsSetupStore
    const { isRubberMode } = this.mapViewSetUpStore.mapBoxEditorStore

    return (
      <div className="relative full-height">
        <AutoSizer>
          {({ width, height }) => (
            <List
              deferredMeasurementCache={this.cellMeasurerCache}
              data={visibleHierarchyList}
              width={width}
              height={height}
              rowCount={visibleHierarchyList.length}
              overscanRowCount={OVERSCAN_ROW_COUNT}
              scrollToAlignment="start"
              rowHeight={ROW_HEIGHT}
              rowRenderer={this.renderRow.bind(null, isRubberMode)}
            />
          )}
        </AutoSizer>
      </div>
    )
  }

  public renderRow = (
    isRubberMode: boolean,
    { key, style, index, parent }: any,
  ): JSX.Element => {
    const {
      mapViewItemsSetupStore,
      sitemapViewsSetupStore,
      mapBoxViewerStore,
      globeViewSetupStore,
      mapBoxEditorStore,
    } = this.mapViewSetUpStore
    const node = mapViewItemsSetupStore.visibleHierarchyList[index]

    if (!node) {
      return null
    }

    return (
      <CellMeasurer
        cache={this.cellMeasurerCache}
        columnIndex={0}
        key={key}
        parent={parent}
        rowIndex={index}
      >
        {({ registerChild }) => (
          <div
            style={style}
            ref={registerChild}
            className="col bb-palette-brand-lighter"
          >
            <HierarchyTreeNode
              node={node}
              key={index}
              mapViewItemsSetupStore={mapViewItemsSetupStore}
              sitemapViewsSetupStore={sitemapViewsSetupStore}
              level={node.level}
              getUpdatedItem={this.getUpdatedItemCoords}
              globeViewSetupStore={globeViewSetupStore}
              setViewportFromItem={mapBoxViewerStore.setViewportFromItem}
              recomputeGridSize={this.recomputeGridSize}
              mapBoxEditorStore={isRubberMode && mapBoxEditorStore}
            />
          </div>
        )}
      </CellMeasurer>
    )
  }

  private getUpdatedItemCoords = (item: MapViewItemBase): MapViewItemBase => {
    const {
      isGlobeMode,
      mapBoxViewerStore: { displayedGlobeViewItems },
    } = this.mapViewSetUpStore
    return isGlobeMode
      ? displayedGlobeViewItems.find(i => item.id == i.id)
      : item
  }

  private renderHierarchyPanel(): JSX.Element {
    const { selectedViewType } = this.mapViewSetUpStore.mapBoxEditorStore

    switch (selectedViewType) {
      case ViewType.Objects:
        return this.renderItemsPanel()
      case ViewType.MapFiles:
        return this.renderMapFiles()
    }
  }

  private get navigationIcon(): JSX.Element {
    const { itemsCollapseState } = this.mapViewSetUpStore.mapViewItemsSetupStore
    const className = 'tw:shrink-0 tw:mr-1'

    switch (itemsCollapseState) {
      case ItemsCollapseState.collapsed:
        return <ChevronsDownIcon size={16} className={className} />
      case ItemsCollapseState.notCollapsed:
        return <ChevronsUpIcon size={16} className={className} />
      default:
        return <ChevronsUpDownIcon size={16} className={className} />
    }
  }

  private onKeyDown = (event: KeyboardEvent): void => {
    if (!isBaseSurfaceActive()) {
      return
    }
    if ((event.metaKey || event.ctrlKey) && event.code === C_KEY_CODE) {
      this.mapViewSetUpStore.mapViewItemsSetupStore.copyLevel()
    }
    if ((event.metaKey || event.ctrlKey) && event.code === V_KEY_CODE) {
      this.mapViewSetUpStore.mapViewItemsSetupStore.pasteLevel()
    }
  }

  @computed
  private get sortableMapFiles(): ISortableListItems {
    const { filteredMapFiles, filteredGlobeViewMapFiles, isMapFileDisplayed } =
      this.store
    return {
      hiddenItems: filteredMapFiles
        .filter(f => !isMapFileDisplayed(f))
        .map(f => this.renderMapFile(f, true)),
      shownItems: filteredGlobeViewMapFiles
        .filter(f => isMapFileDisplayed(f))
        .map(f =>
          this.renderMapFile(
            f,
            this.mapViewSetUpStore.mapBoxEditorStore.isRubberMode,
          ),
        ),
    }
  }

  @action
  private onMouseEnterFile = (f: IMapFile): void => {
    this.hoveredMapFile = f
  }

  @action
  private onMouseLeaveFile = (): void => {
    this.hoveredMapFile = null
  }

  private shouldHighlightAllBorders(f: IMapFile): boolean {
    const { sitemapsSetupStore, tilesetsSetupStore, remoteTilesetsSetupStore } =
      this.mapViewSetUpStore
    switch (f.type) {
      case MapFileType.Sitemap:
        return sitemapsSetupStore.selectedSitemap?.id === f.id
      case MapFileType.Tileset:
        return tilesetsSetupStore.selectedTilesetId === f.id
      case MapFileType.RemoteTileset:
        return remoteTilesetsSetupStore.selectedRemoteTilesetId === f.id
      default:
        return false
    }
  }

  private getDisplayedExtension(f: IMapFile): string {
    const splittedName = f.fileName.split('.')
    const ext = splittedName?.length > 1 ? splittedName?.at(-1) : ''
    switch (f.type) {
      case MapFileType.Sitemap:
        return ext || Localization.translator.image
      case MapFileType.Tileset:
        return ext || tileset
      case MapFileType.RemoteTileset:
        return remoteTileset
      default:
        return file
    }
  }

  private renderMapFile(f: IMapFile, isHidden?: boolean): JSX.Element {
    const shouldHighlightAllBorders = this.shouldHighlightAllBorders(f)

    const name = mapFileDisplayName(f.name)
    const ext = this.getDisplayedExtension(f)
    return (
      <div
        key={f.id}
        className={classList({
          'globe-plan-node row pr20 relative pl12 pointer': true,
          h70: !!f.filledImage,
          h50: !f.filledImage,
          selected: shouldHighlightAllBorders,
        })}
        onMouseEnter={this.onMouseEnterFile.bind(this, f)}
        onMouseLeave={this.onMouseLeaveFile}
        onClick={
          this.mapViewSetUpStore.mapBoxEditorStore.isRubberMode
            ? NOOP
            : this.selectMapFile.bind(this, f)
        }
      >
        <div className="row">
          {!isHidden && (
            <GripVerticalIcon
              size={16}
              className="tw:shrink-0 tw:text-muted-foreground"
            />
          )}
          <div className="col full-height sitemap-image-holder x-center no-grow unclickable-element">
            {this.getMapFileIcon(f)}
          </div>
          <div className="row full-height y-center sitemap-item-name">
            <div className="col px10">
              <div className="row full-width relative">
                <div
                  className={classList({
                    'text medium-bold brada4 text-ellipsis bg-unset': true,
                    'max-width160': !f.filledImage,
                    'max-width130': !!f.filledImage,
                  })}
                >
                  {name}
                </div>
              </div>
              <div className="text row grey">{ext.toLocaleUpperCase()}</div>
            </div>
          </div>
        </div>
        {this.selectedGlobeView && this.renderMapFileObjectVisibilityToggle(f)}
      </div>
    )
  }

  private getMapFileIcon = (f: IMapFile): JSX.Element => {
    switch (f.type) {
      case MapFileType.Sitemap:
        return (
          <DesktopFileInput
            id={f.id}
            projectId={this.localProps.state.activeProject.id}
            name=""
            value={f.filledImage}
            isReadonly={true}
            textClassName="hint"
            shouldHideIconAndOutline={true}
          />
        )
      case MapFileType.Tileset:
        return (
          <Icons.File className="h36 w36 brada3 ba-light-grey bg-white pa8 icon-dark-grey" />
        )
      case MapFileType.RemoteTileset:
        return (
          <span className="tw:flex tw:size-9 tw:items-center tw:justify-center tw:rounded-[3px] tw:border tw:border-solid tw:border-border tw:bg-card">
            <LinkIcon size={20} />
          </span>
        )
      default:
        return <Icons.File className="h20 w20" />
    }
  }

  private renderMapFiles(): JSX.Element {
    return (
      <>
        {this.renderMapFilesSearchBar()}
        <div className="scrollable full-height">
          {this.renderMapFilesList()}
        </div>
      </>
    )
  }

  private renderMapFilesList(): JSX.Element {
    return (
      <>
        <div className="row pa10">
          <div className="text large">
            {Localization.translator.xFiles(this.store.filteredMapFiles.length)}
          </div>
          {this.renderMapNotificationsButton()}
          {this.renderRemoteTilesetCreationButton()}
          {this.renderMapFileCreationButton()}
        </div>
        <div className="col pb12 columns-list no-select">
          <div className="col full-height sortable-list-holder">
            <SortableList
              items={this.sortableMapFiles}
              onSortEnd={this.store.onMapFilesSortEnd}
              axis="y"
              distance={2}
              isRubberMode={
                this.mapViewSetUpStore.mapBoxEditorStore.isRubberMode
              }
            />
          </div>
        </div>
      </>
    )
  }

  private selectMapFile = async (f: IMapFile): Promise<void> => {
    const {
      tilesetsSetupStore,
      remoteTilesetsSetupStore,
      sitemapsSetupStore,
      mapBoxViewerStore,
    } = this.mapViewSetUpStore
    tilesetsSetupStore.deselectTileset()
    sitemapsSetupStore.deselectSitemap()
    remoteTilesetsSetupStore.deselectRemoteTileset()
    mapBoxViewerStore.tilesetsControlStore.resetTilesetFeaturesSelection()
    if (f.type === MapFileType.Sitemap) {
      await sitemapsSetupStore.selectSitemapById(f.id, false)
      mapBoxViewerStore.setViewportFromPlan(sitemapsSetupStore.selectedSitemap)
    }
    if (f.type === MapFileType.Tileset) {
      await tilesetsSetupStore.selectTilesetById(f.id, false)
      mapBoxViewerStore.setViewportFromTileset(
        tilesetsSetupStore.selectedTileset,
      )
    }
    if (f.type === MapFileType.RemoteTileset) {
      await remoteTilesetsSetupStore.selectRemoteTilesetById(f.id, false)
    }
  }

  private renderMapFileObjectVisibilityToggle = (f: IMapFile): JSX.Element => {
    const isDisplayed = this.store.isMapFileDisplayed(f)
    let onClick = null
    switch (f.type) {
      case MapFileType.Sitemap:
        onClick = this.onPlanVisibilityClick
        break
      case MapFileType.Tileset:
        onClick = this.onTilesetVisibilityClick
        break
      case MapFileType.RemoteTileset:
        onClick = this.onRemoteTilesetVisibilityClick
    }

    return (
      <div
        className="icon-wrapper no-grow pointer"
        onClick={onClick.bind(null, f.id)}
      >
        {isDisplayed ? (
          this.hoveredMapFile?.id === f.id && (
            <EyeIcon size={16} className="tw:text-muted-foreground" />
          )
        ) : (
          <EyeOffIcon size={16} className="tw:text-muted-foreground" />
        )}
      </div>
    )
  }

  @action.bound
  private onPlanVisibilityClick(sitemapId: string) {
    const { globeViewSetupStore, mapBoxEditorStore } = this.mapViewSetUpStore
    if (mapBoxEditorStore.isRubberMode) {
      mapBoxEditorStore.togglePlanVisibility(sitemapId)
    } else {
      globeViewSetupStore.setSitemapToGlobeView(sitemapId)
    }
  }

  @action.bound
  private onTilesetVisibilityClick(tilesetId: string) {
    const { globeViewSetupStore, mapBoxEditorStore } = this.mapViewSetUpStore
    if (mapBoxEditorStore.isRubberMode) {
      mapBoxEditorStore.toggleTilesetVisibility(tilesetId)
    } else {
      globeViewSetupStore.setTilesetToGlobeView(tilesetId)
    }
  }

  @action.bound
  private onRemoteTilesetVisibilityClick(tilesetId: string) {
    const { globeViewSetupStore, mapBoxEditorStore } = this.mapViewSetUpStore
    if (mapBoxEditorStore.isRubberMode) {
      mapBoxEditorStore.toggleRemoteTilesetVisibility(tilesetId)
    } else {
      globeViewSetupStore.setRemoteTilesetToGlobeView(tilesetId)
    }
  }

  private renderMapFileCreationButton(): JSX.Element {
    const { toggleCreateMapFileMenu } =
      this.mapViewSetUpStore.sitemapControlStore

    return (
      <Button
        variant="ghost"
        size="icon-sm"
        className="tw:flex-none!"
        title={Localization.translator.mapFileUpload.uploadMapFile}
        aria-label={Localization.translator.mapFileUpload.uploadMapFile}
        onClick={toggleCreateMapFileMenu.bind(this)}
      >
        <PlusIcon />
      </Button>
    )
  }

  private renderRemoteTilesetCreationButton(): JSX.Element {
    const { toggleCreateRemoteTilesetMenu } =
      this.mapViewSetUpStore.sitemapControlStore

    return (
      <Button
        variant="ghost"
        size="icon-sm"
        className="tw:flex-none!"
        title={Localization.translator.addRemoteTileset}
        aria-label={Localization.translator.addRemoteTileset}
        onClick={toggleCreateRemoteTilesetMenu.bind(this)}
      >
        <LinkIcon />
      </Button>
    )
  }

  private renderMapNotificationsButton(): JSX.Element {
    const { tilesetUploadProgressStore } = this.props
    const activeUploadsCount =
      tilesetUploadProgressStore?.activeUploads?.length || 0
    const failedUploadsCount =
      tilesetUploadProgressStore?.failedUploads?.length || 0
    const notificationsCount = this.store.tilesetsForNotifications?.length || 0
    const totalCount =
      activeUploadsCount + failedUploadsCount + notificationsCount

    return (
      <div className="tw:flex-none!">
        <Popover
          open={this.store.isMapNotificationModalOpened}
          onOpenChange={open => {
            if (open !== this.store.isMapNotificationModalOpened) {
              this.store.toggleIsMapNotificationModalOpened()
            }
          }}
        >
          <PopoverTrigger
            render={<Button variant="ghost" size="icon-sm" />}
            title={Localization.translator.mapNotifications}
            aria-label={Localization.translator.mapNotifications}
            className="tw:relative"
          >
            <HistoryIcon />
            {!!totalCount && (
              <span className="tw:absolute tw:-top-0.5 tw:left-1/2 tw:flex tw:h-[15px] tw:min-w-[15px] tw:items-center tw:justify-center tw:rounded-full tw:border tw:border-solid tw:border-background tw:bg-destructive tw:px-1 tw:text-[10px] tw:leading-none tw:text-destructive-foreground">
                {totalCount}
              </span>
            )}
          </PopoverTrigger>
          <PopoverContent
            side="bottom"
            align="end"
            className="tw:w-auto tw:bg-transparent tw:p-0 tw:shadow-none tw:ring-0"
          >
            <MapNotificationsPopoverContent
              tilesetUploadProgressStore={tilesetUploadProgressStore}
              tilesetsForNotifications={this.store.tilesetsForNotifications}
              onClose={this.store.toggleIsMapNotificationModalOpened}
              onHideNotification={this.store.hideNotification}
            />
          </PopoverContent>
        </Popover>
      </div>
    )
  }

  private get selectedGlobeView(): GlobeView {
    return this.mapViewSetUpStore.globeViewSetupStore.selectedGlobeView
  }

  @action.bound
  private toggleCollapsing(): void {
    this.mapViewSetUpStore.mapViewItemsSetupStore.toggleItemsCollapsingState()
    this.recomputeGridSize()
  }

  @action.bound
  private recomputeGridSize(): void {
    this.cellMeasurerCache?.clearAll()
  }
}
